import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Settings, Lock, Eye, EyeOff, Loader2, 
  Check, User, Camera, Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Compila tutti i campi' });
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'La nuova password deve essere almeno 6 caratteri' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Le password non coincidono' });
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setPasswordMessage({ type: 'success', text: 'Password cambiata con successo!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Optional: logout after password change
      // setTimeout(() => { logout(); navigate('/login'); }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore durante il cambio password';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Impostazioni
              </h1>
              <p className="text-sm text-gray-500">Gestisci le impostazioni del tuo account</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        
        {/* Change Password Section */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
          <div className="bg-gray-800 px-6 py-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-3">
              <Lock className="w-5 h-5" />
              Cambia Password Admin
            </h2>
          </div>
          
          <form onSubmit={handleChangePassword} className="p-6 space-y-4">
            {/* Message */}
            {passwordMessage.text && (
              <div className={`p-4 rounded-lg flex items-center gap-2 ${
                passwordMessage.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {passwordMessage.type === 'success' ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Shield className="w-5 h-5" />
                )}
                {passwordMessage.text}
              </div>
            )}
            
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password Attuale
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent pr-12"
                  placeholder="Inserisci password attuale"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nuova Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent pr-12"
                  placeholder="Inserisci nuova password (min. 6 caratteri)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conferma Nuova Password
              </label>
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                placeholder="Ripeti nuova password"
              />
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isChangingPassword}
              className="w-full py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Cambio in corso...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Cambia Password
                </>
              )}
            </button>
          </form>
        </section>
        
        {/* Account Info Section */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
          <div className="bg-gray-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-3">
              <User className="w-5 h-5" />
              Informazioni Account
            </h2>
          </div>
          
          <div className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                A
              </div>
              <div>
                <p className="text-lg font-medium text-gray-800">Admin</p>
                <p className="text-sm text-gray-500">Fotografo / Amministratore</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Future Settings Placeholder */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden opacity-50">
          <div className="bg-gray-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-3">
              <Camera className="w-5 h-5" />
              Altre Impostazioni
            </h2>
          </div>
          
          <div className="p-6 text-center text-gray-500">
            <p>Altre impostazioni saranno disponibili in futuro</p>
          </div>
        </section>
        
      </div>
    </div>
  );
};

export default AdminSettingsPage;
